import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Container, NavDropdown, Dropdown, Button } from 'react-bootstrap';
import Icon from '../common/Icons';
import { useToast } from '../../hooks/useToast';
import { useServerHeaderClock } from '../../hooks/useServerHeaderClock';
import { ReceptionToastStack } from '../receptionist/ui';
import { useAuth } from '../../context/AuthContext';
import MedicalPrescriptionForm from './MedicalPrescriptionForm';
import { authService } from '../../services/api';
import { ROLES, getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import {
  getDoctorNavItems,
  getDoctorMenuModel,
  physicianCanPrescribe,
  isHeadDoctorRole,
  DOCTOR_PATHS,
  DENTIST_PATHS
} from './doctorPaths';
import { DOCTOR_COPY } from './doctorCopy';
import { DoctorWorkspaceContext } from './doctorWorkspaceContext';
import { CLINICAL_WORK_SITE_SESSION_KEY } from '../nurse/nurseWorkspaceStorage';
import '../receptionist/ReceptionistDashboard.css';
import '../head-doctor/HeadDoctorDashboard.css';
import './PhysicianDashboard.css';
import './DoctorDashboard.css';
import { HEAD_DOCTOR_COPY } from '../head-doctor/headDoctorCopy';
import HeadDoctorNavMenu from '../head-doctor/HeadDoctorNavMenu';
import { canRoleAccessLocalPharmacy } from '../../constants/siteCareModes';

const HD_SIDEBAR_STORAGE_KEY = 'hd-sidebar-collapsed';
const PH_SIDEBAR_STORAGE_KEY = 'ph-sidebar-collapsed';
const DENTIST_SIDEBAR_STORAGE_KEY = 'dentist-sidebar-collapsed';

/** Évite le fallback popper "static" (menu mal placé / coupé) sous shell overflow:hidden */
const HD_USER_MENU_POPPER_CONFIG = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: { boundary: 'viewport', padding: 8 }
    },
    {
      name: 'offset',
      options: { offset: [0, 8] }
    }
  ]
};

function readSidebarCollapsed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

function PhysicianNavMenu({ items, collapsed = false }) {
  return (
    <div className="ph-menu-section">
      <div className="ph-menu-section__items">
        {items.map(({ to, end, label, hint, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            title={collapsed ? label : undefined}
            className={({ isActive }) => `recv-menu-item ph-menu-item${isActive ? ' is-active' : ''}`}
          >
            <span className="recv-menu-item__icon ph-menu-item__icon" aria-hidden>
              <Icon name={icon || 'home'} size={18} />
            </span>
            <span className="recv-menu-item__text ph-menu-item__text">
              <span className="recv-menu-item__label ph-menu-item__label">{label}</span>
              {hint ? <span className="recv-menu-item__hint ph-menu-item__hint">{hint}</span> : null}
            </span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function DoctorLayout({
  portalPaths = DOCTOR_PATHS,
  forceHeadDoctor = false,
  forceDentist = false
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const canPrescribe = physicianCanPrescribe(user?.role);
  const isDentistPortal = forceDentist;
  const isHeadDoctor = forceHeadDoctor || isHeadDoctorRole(user?.role);
  const isHeadPortal = isHeadDoctor && !isDentistPortal;
  const isPhysicianPortal = !isHeadPortal;
  const layoutCopy = isHeadPortal ? HEAD_DOCTOR_COPY.LAYOUT : DOCTOR_COPY.LAYOUT;
  const sidebarStorageKey = isHeadPortal
    ? HD_SIDEBAR_STORAGE_KEY
    : isDentistPortal
      ? DENTIST_SIDEBAR_STORAGE_KEY
      : PH_SIDEBAR_STORAGE_KEY;

  const { toasts, showToast, removeToast, getDuration } = useToast();
  const [workSites, setWorkSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [workSiteId, setWorkSiteId] = useState('');
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [statsReloadNonce, setStatsReloadNonce] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readSidebarCollapsed(
      forceHeadDoctor && !forceDentist
        ? HD_SIDEBAR_STORAGE_KEY
        : forceDentist
          ? DENTIST_SIDEBAR_STORAGE_KEY
          : PH_SIDEBAR_STORAGE_KEY
    )
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const workSiteSessionKey = useMemo(() => {
    const userKey = user?.id || user?.email || user?.username || 'anonymous';
    return `${isHeadPortal ? 'head-doctor' : 'doctor'}-work-site:${userKey}`;
  }, [isHeadPortal, user?.email, user?.id, user?.username]);
  const currentSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );
  const showLocalPharmacy = canRoleAccessLocalPharmacy(user?.role, currentSite);

  const navItems = useMemo(
    () => getDoctorNavItems({ canPrescribe, isHeadDoctor, paths: portalPaths, showLocalPharmacy }),
    [canPrescribe, isHeadDoctor, portalPaths, showLocalPharmacy]
  );
  const menuModel = useMemo(
    () => getDoctorMenuModel({ canPrescribe, isHeadDoctor, paths: portalPaths, showLocalPharmacy }),
    [canPrescribe, isHeadDoctor, portalPaths, showLocalPharmacy]
  );
  const headDoctorMenuSections = useMemo(
    () => (isHeadPortal ? menuModel : null),
    [isHeadPortal, menuModel]
  );

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(sidebarStorageKey, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [sidebarStorageKey]);

  useEffect(() => {
    if (!isHeadPortal || !mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileNav();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isHeadPortal, mobileNavOpen, closeMobileNav]);

  const toast = useCallback((variant, msg) => {
    showToast(msg, variant);
  }, [showToast]);

  const headerClock = useServerHeaderClock(Boolean(user) && isPhysicianPortal);

  const loadWorkSites = useCallback(() => {
    let cancelled = false;
    (async () => {
      setSitesLoading(true);
      setSitesError('');
      try {
        const { sites } = await authService.getProfile();
        if (!cancelled) setWorkSites(Array.isArray(sites) ? sites : []);
      } catch (error) {
        if (!cancelled) {
          setWorkSites([]);
          setSitesError(error.message || 'Impossible de charger vos sites de travail');
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return loadWorkSites();
  }, [loadWorkSites]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!workSites.length) return;
    const valid = workSites.some((s) => String(s.id) === String(workSiteId));
    if (valid && workSiteId) return;
    const stored = window.sessionStorage.getItem(workSiteSessionKey);
    if (stored && workSites.some((s) => String(s.id) === String(stored))) {
      setWorkSiteId(String(stored));
      try {
        sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, String(stored));
      } catch {
        /* ignore */
      }
      return;
    }
    if (workSites.length === 1) {
      const onlyId = String(workSites[0].id);
      setWorkSiteId(onlyId);
      try {
        sessionStorage.setItem(workSiteSessionKey, onlyId);
        sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, onlyId);
      } catch {
        /* ignore */
      }
      return;
    }
    setWorkSiteId('');
  }, [workSiteSessionKey, workSites, workSiteId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!workSiteId) return;
    try {
      window.sessionStorage.setItem(workSiteSessionKey, workSiteId);
      window.sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, workSiteId);
    } catch {
      /* ignore */
    }
  }, [workSiteId, workSiteSessionKey]);

  const bumpDashboardStats = useCallback(() => {
    setStatsReloadNonce((n) => n + 1);
  }, []);

  const handleWorkSiteChange = useCallback((id) => {
    setWorkSiteId(id);
    try {
      if (id) {
        sessionStorage.setItem(workSiteSessionKey, id);
        sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, id);
      } else {
        sessionStorage.removeItem(workSiteSessionKey);
        sessionStorage.removeItem(CLINICAL_WORK_SITE_SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [workSiteSessionKey]);

  const chooseWorkSite = useCallback(
    (site) => {
      handleWorkSiteChange(String(site.id));
      toast('success', layoutCopy.SITE_ACTIVE_TOAST.replace('{name}', site.name));
    },
    [handleWorkSiteChange, layoutCopy.SITE_ACTIVE_TOAST, toast]
  );

  const resetWorkSite = useCallback(() => {
    handleWorkSiteChange('');
    closeMobileNav();
  }, [closeMobileNav, handleWorkSiteChange]);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(workSiteSessionKey);
      sessionStorage.removeItem(CLINICAL_WORK_SITE_SESSION_KEY);
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const clinicalWorkspace = forceDentist ? 'dentist' : isHeadPortal ? 'headDoctor' : 'physician';
  const shouldShowSiteGate = !currentSite;

  const outletValue = useMemo(
    () => ({
      workSites,
      workSiteId,
      setWorkSiteId: handleWorkSiteChange,
      bumpDashboardStats,
      toast,
      statsReloadNonce,
      openNewConsultation: () => navigate(portalPaths.CONSULTATION_NEW),
      openNewPrescription: () => setShowPrescriptionModal(true),
      canPrescribe,
      portalPaths,
      clinicalWorkspace,
      showLocalPharmacy
    }),
    [
      workSites,
      workSiteId,
      handleWorkSiteChange,
      bumpDashboardStats,
      toast,
      statsReloadNonce,
      navigate,
      canPrescribe,
      portalPaths,
      clinicalWorkspace,
      showLocalPharmacy
    ]
  );

  if (!user) {
    return null;
  }

  if (user.role === ROLES.NURSE) {
    return <Navigate to="/nurse" replace />;
  }

  if (user.role === ROLES.DENTIST && !forceDentist) {
    return <Navigate to={DENTIST_PATHS.DASHBOARD} replace />;
  }

  if (forceDentist && user.role !== ROLES.DENTIST) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (
    !forceDentist &&
    !forceHeadDoctor &&
    user.role === ROLES.HEAD_DOCTOR &&
    portalPaths === DOCTOR_PATHS
  ) {
    return <Navigate to="/head-doctor" replace />;
  }

  const isDentist = isDentistPortal || user.role === ROLES.DENTIST;
  const sideMenuTitle = isDentist
    ? DOCTOR_COPY.LAYOUT.SIDE_MENU_SECTION_DENTIST
    : DOCTOR_COPY.LAYOUT.SIDE_MENU_SECTION_PHYSICIAN;
  const topbarSection = isDentist
    ? DOCTOR_COPY.LAYOUT.TOPBAR_SECTION_DENTIST
    : DOCTOR_COPY.LAYOUT.TOPBAR_SECTION_PHYSICIAN;

  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;
  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() || 'M';
  const displayName =
    `${user?.last_name || ''}${user?.first_name ? ` ${user.first_name}` : ''}`.trim() ||
    user?.name ||
    DOCTOR_COPY.LAYOUT.ACCOUNT_FALLBACK;

  const workSiteChip = currentSite ? (
    <button
      type="button"
      className="recv-site-chip mx-lg-2 my-2 my-lg-0"
      onClick={resetWorkSite}
      title={layoutCopy.CHANGE_SITE_CHIP_TITLE}
    >
      <span className="recv-site-chip__label">{layoutCopy.SITE_CHIP_LABEL}</span>
      <span className="recv-site-chip__name">{currentSite.name}</span>
    </button>
  ) : workSites.length === 0 && !sitesLoading ? (
    <span className={isHeadPortal ? 'hd-topbar__no-site' : 'text-white-50 small px-2'}>
      {layoutCopy.NO_SITE_ASSIGNED}
    </span>
  ) : null;

  const siteGateMain = (
    <main className="recv-site-gate flex-grow-1 overflow-auto">
      <Container fluid className="recv-site-gate__container">
        <section className="recv-site-gate__panel" aria-labelledby="doctor-site-gate-title">
          <div className="recv-site-gate__intro">
            <span className="recv-site-gate__badge">{layoutCopy.SITE_GATE_BADGE}</span>
            <h1 id="doctor-site-gate-title">{layoutCopy.SITE_GATE_TITLE}</h1>
            <p>{layoutCopy.SITE_GATE_DESC}</p>
          </div>

          {sitesLoading ? (
            <div className="recv-site-gate__state">
              <span className="spinner-border spinner-border-sm" aria-hidden />
              {layoutCopy.SITES_LOADING}
            </div>
          ) : sitesError ? (
            <div className="recv-site-gate__state recv-site-gate__state--error">
              <span>{sitesError}</span>
              <Button variant="outline-primary" size="sm" onClick={loadWorkSites}>
                Réessayer
              </Button>
            </div>
          ) : workSites.length === 0 ? (
            <div className="recv-site-gate__state recv-site-gate__state--error">
              Aucun site de travail n&apos;est affecté à votre profil. Contactez un administrateur pour vous
              attribuer au moins un site actif.
            </div>
          ) : (
            <div className="recv-site-grid" role="list">
              {workSites.map((site) => (
                <button
                  key={site.id}
                  type="button"
                  className="recv-site-card"
                  onClick={() => chooseWorkSite(site)}
                  role="listitem"
                >
                  <span className="recv-site-card__icon" aria-hidden>
                    <Icon name="building" size={18} />
                  </span>
                  <span className="recv-site-card__body">
                    <span className="recv-site-card__name">{site.name}</span>
                    <span className="recv-site-card__meta">
                      {site.address || site.location || layoutCopy.SITE_CARD_META_FALLBACK}
                    </span>
                  </span>
                  <span className="recv-site-card__cta">{layoutCopy.SITE_CARD_CTA}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </Container>
    </main>
  );

  const headDoctorUserMenu = (
    <Dropdown align="end" className="hd-user-menu">
      <Dropdown.Toggle
        id="hd-user-menu"
        variant="link"
        className="hd-user-menu__toggle-btn"
        aria-label={HEAD_DOCTOR_COPY.ARIA_USER_MENU}
      >
        <span className="hd-user-menu__trigger">
          <span className="hd-user-menu__avatar" aria-hidden>
            {initials}
          </span>
          <span className="hd-user-menu__meta d-none d-md-flex">
            <span className="hd-user-menu__name">{displayName}</span>
            <span className="hd-user-menu__role">{getRoleLabel(user?.role)}</span>
          </span>
          <Icon name="chevronDown" size={14} className="hd-user-menu__chevron d-none d-md-block" aria-hidden />
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" popperConfig={HD_USER_MENU_POPPER_CONFIG}>
        <Dropdown.Header className="hd-user-menu__header">
          <span className="hd-user-menu__header-avatar" aria-hidden>
            {initials}
          </span>
          <span className="hd-user-menu__header-text">
            <span className="hd-user-menu__header-name">{displayName}</span>
            {user?.email ? <span className="hd-user-menu__header-email">{user.email}</span> : null}
            <span className="hd-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
          </span>
        </Dropdown.Header>
        {currentSite ? (
          <>
            <Dropdown.Divider className="hd-user-menu__divider" />
            <Dropdown.Item className="hd-user-menu__action" onClick={resetWorkSite}>
              {layoutCopy.CHANGE_SITE_MENU}
            </Dropdown.Item>
          </>
        ) : null}
        <Dropdown.Divider className="hd-user-menu__divider" />
        <Dropdown.Item
          className="hd-user-menu__action hd-user-menu__action--danger"
          onClick={handleLogout}
        >
          <Icon name="signOut" size={16} aria-hidden />
          Déconnexion
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <div
      className={`receptionist-dashboard receptionist-layout min-vh-100 d-flex flex-column ${
        isHeadPortal
          ? `head-doctor-portal head-doctor-layout${sidebarCollapsed ? ' hd-sidebar-is-collapsed' : ''}`
          : `physician-portal physician-clinical-portal physician-clinical-layout${
              isDentistPortal ? ' physician-clinical-portal--dentist' : ''
            }${sidebarCollapsed ? ' ph-sidebar-is-collapsed' : ''}`
      }`}
    >
      {isHeadPortal ? (
        <Navbar variant="dark" expand="lg" className="hd-topbar flex-shrink-0 recv-no-print shadow-sm">
          <Container fluid className="hd-topbar__container">
            <Navbar.Brand
              className="hd-topbar__brand-wrap mb-0"
              onClick={() => {
                closeMobileNav();
                navigate(portalPaths.DASHBOARD);
              }}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
                alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
                className="hd-topbar__wordmark"
              />
              <span className="hd-topbar__brand">
                <span className="hd-topbar__brand-product">{ACCENT}</span>
                <span className="hd-topbar__brand-section">{HEAD_DOCTOR_COPY.LAYOUT.NAVBAR_BRAND}</span>
              </span>
            </Navbar.Brand>

            <div className="hd-topbar__toolbar ms-auto d-flex align-items-center gap-2">
              {workSiteChip}
              {headDoctorUserMenu}
              {currentSite ? (
                <button
                  type="button"
                  className={`hd-topbar__toggle d-lg-none navbar-toggler ${
                    mobileNavOpen ? 'is-open' : ''
                  }`}
                  aria-controls="hd-mobile-drawer"
                  aria-expanded={mobileNavOpen}
                  aria-label={
                    mobileNavOpen ? HEAD_DOCTOR_COPY.ARIA_CLOSE_MENU : HEAD_DOCTOR_COPY.ARIA_TOGGLE_MENU
                  }
                  onClick={() => (mobileNavOpen ? closeMobileNav() : openMobileNav())}
                >
                  {mobileNavOpen ? (
                    <Icon name="times" size={20} className="hd-topbar__toggle-icon" aria-hidden />
                  ) : (
                    <span className="navbar-toggler-icon" />
                  )}
                </button>
              ) : null}
            </div>
          </Container>
        </Navbar>
      ) : (
        <Navbar variant="dark" className="recv-topbar ph-topbar flex-shrink-0 recv-no-print shadow-sm">
          <Container fluid className="recv-topbar__container ph-topbar__container">
            <Navbar.Brand
              className="recv-topbar__brand-wrap ph-topbar__brand-wrap mb-0"
              onClick={() => navigate(portalPaths.DASHBOARD)}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
                alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
                className="recv-topbar__wordmark ph-topbar__wordmark"
              />
              <span className="recv-topbar__brand ph-topbar__brand">
                <span className="recv-topbar__brand-product ph-topbar__brand-product">{ACCENT}</span>
                <span className="recv-topbar__brand-section ph-topbar__brand-section">{topbarSection}</span>
              </span>
            </Navbar.Brand>

            <div className="recv-topbar__toolbar ph-topbar__toolbar ms-auto d-flex align-items-center gap-2">
              {workSiteChip}
              <span
                className="recv-topbar__clock d-none d-md-inline recv-header-clock"
                title="Heure serveur"
                aria-live="polite"
              >
                {headerClock}
              </span>
              <NavDropdown
                align="end"
                className="recv-user-menu ph-user-menu"
                title={
                  <span
                    className="recv-user-menu__trigger"
                    aria-label={DOCTOR_COPY.LAYOUT.ACCOUNT_MENU_ARIA}
                  >
                    <span className="recv-user-menu__avatar" aria-hidden>
                      {initials}
                    </span>
                    <span className="recv-user-menu__meta d-none d-md-flex">
                      <span className="recv-user-menu__name">{displayName}</span>
                      <span className="recv-user-menu__role">{getRoleLabel(user?.role)}</span>
                    </span>
                    <Icon
                      name="chevronDown"
                      size={14}
                      className="recv-user-menu__chevron d-none d-md-block"
                      aria-hidden
                    />
                  </span>
                }
                id="physician-user-menu"
              >
                <NavDropdown.Header className="recv-user-menu__header">
                  <span className="recv-user-menu__header-avatar" aria-hidden>
                    {initials}
                  </span>
                  <span className="recv-user-menu__header-text">
                    <span className="recv-user-menu__header-name">{displayName}</span>
                    {user?.email ? (
                      <span className="recv-user-menu__header-email">{user.email}</span>
                    ) : null}
                    <span className="recv-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
                  </span>
                </NavDropdown.Header>
                {currentSite ? (
                  <>
                    <NavDropdown.Divider className="recv-user-menu__divider" />
                    <NavDropdown.Item className="recv-user-menu__action" onClick={resetWorkSite}>
                      {layoutCopy.CHANGE_SITE_MENU}
                    </NavDropdown.Item>
                  </>
                ) : null}
                <NavDropdown.Divider className="recv-user-menu__divider" />
                <NavDropdown.Item
                  className="recv-user-menu__action recv-user-menu__action--danger"
                  onClick={handleLogout}
                >
                  <Icon name="signOut" size={16} aria-hidden />
                  Déconnexion
                </NavDropdown.Item>
              </NavDropdown>
            </div>
          </Container>
        </Navbar>
      )}

      {shouldShowSiteGate ? (
        siteGateMain
      ) : (
        <>
          {isHeadPortal && mobileNavOpen ? (
            <>
              <button
                type="button"
                className="hd-mobile-drawer__backdrop recv-no-print"
                aria-label={HEAD_DOCTOR_COPY.ARIA_CLOSE_MENU}
                onClick={closeMobileNav}
              />
              <aside
                id="hd-mobile-drawer"
                className="hd-mobile-drawer recv-no-print"
                role="dialog"
                aria-modal="true"
                aria-label={HEAD_DOCTOR_COPY.MOBILE_NAV_TITLE}
              >
                <div className="hd-mobile-drawer__head">
                  <span className="hd-mobile-drawer__title">{HEAD_DOCTOR_COPY.MOBILE_NAV_TITLE}</span>
                  <button
                    type="button"
                    className="hd-mobile-drawer__close"
                    aria-label={HEAD_DOCTOR_COPY.ARIA_CLOSE_MENU}
                    onClick={closeMobileNav}
                  >
                    <Icon name="times" size={18} />
                  </button>
                </div>
                <nav className="hd-sidebar__nav hd-mobile-drawer__nav" aria-label="Navigation principale">
                  <HeadDoctorNavMenu
                    menuModel={headDoctorMenuSections}
                    onNavigate={closeMobileNav}
                    mobile
                  />
                </nav>
              </aside>
            </>
          ) : null}

          {!isHeadPortal ? (
            <div className="d-lg-none recv-mobile-nav ph-mobile-nav border-bottom bg-white flex-shrink-0 recv-no-print">
              <Container fluid className="py-2">
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  {navItems.map(({ to, end, label, hint }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={!!end}
                      title={hint || label}
                      className={({ isActive }) => `recv-mobile-pill ${isActive ? 'is-active' : ''}`}
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              </Container>
            </div>
          ) : null}

          <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
            {isHeadPortal ? (
              <aside
                className={`recv-sidebar hd-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
                  sidebarCollapsed ? ' is-collapsed' : ''
                }`}
                aria-label={HEAD_DOCTOR_COPY.LAYOUT.SIDE_MENU_SECTION}
              >
                <nav className="hd-sidebar__nav" aria-label="Navigation principale">
                  <HeadDoctorNavMenu
                    menuModel={headDoctorMenuSections}
                    collapsed={sidebarCollapsed}
                  />
                </nav>
                <div className="hd-sidebar__footer">
                  <button
                    type="button"
                    className="hd-sidebar__toggle"
                    onClick={toggleSidebar}
                    aria-expanded={!sidebarCollapsed}
                    aria-label={
                      sidebarCollapsed
                        ? HEAD_DOCTOR_COPY.ARIA_EXPAND_SIDEBAR
                        : HEAD_DOCTOR_COPY.ARIA_TOGGLE_SIDEBAR
                    }
                  >
                    <Icon
                      name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'}
                      size={18}
                      aria-hidden
                    />
                    <span className="hd-sidebar__toggle-label">
                      {sidebarCollapsed ? HEAD_DOCTOR_COPY.SIDEBAR_EXPAND : HEAD_DOCTOR_COPY.SIDEBAR_COLLAPSE}
                    </span>
                  </button>
                </div>
              </aside>
            ) : (
              <aside
                className={`recv-sidebar ph-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
                  sidebarCollapsed ? ' is-collapsed' : ''
                }`}
                aria-label={sideMenuTitle}
              >
                <div className="ph-sidebar__brand">
                  <span className="ph-sidebar__brand-label">FUNTOA SMIE</span>
                  <span className="ph-sidebar__brand-title">{sideMenuTitle}</span>
                </div>
                <nav
                  className="recv-sidebar__nav ph-sidebar__nav"
                  aria-label={DOCTOR_COPY.LAYOUT.SIDEBAR_NAV_ARIA}
                >
                  <PhysicianNavMenu items={navItems} collapsed={sidebarCollapsed} />
                </nav>
                <div className="recv-sidebar__footer">
                  <button
                    type="button"
                    className="recv-sidebar__toggle"
                    onClick={toggleSidebar}
                    aria-expanded={!sidebarCollapsed}
                    aria-label={
                      sidebarCollapsed
                        ? DOCTOR_COPY.LAYOUT.SIDEBAR_TOGGLE_COLLAPSED
                        : DOCTOR_COPY.LAYOUT.SIDEBAR_TOGGLE_EXPANDED
                    }
                  >
                    <Icon
                      name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'}
                      size={18}
                      aria-hidden
                    />
                    <span className="recv-sidebar__toggle-label">
                      {sidebarCollapsed
                        ? DOCTOR_COPY.LAYOUT.SIDEBAR_TOGGLE_COLLAPSED_SHORT
                        : DOCTOR_COPY.LAYOUT.SIDEBAR_TOGGLE_EXPANDED_SHORT}
                    </span>
                  </button>
                </div>
              </aside>
            )}

            <main className="recv-main flex-grow-1 overflow-auto">
              <Container
                fluid
                className={`py-4 px-md-4 recv-dash-container ${
                  isHeadPortal ? 'hd-dash-container' : 'ph-dash-container'
                }`}
              >
                <DoctorWorkspaceContext.Provider value={outletValue}>
                  <Outlet />
                </DoctorWorkspaceContext.Provider>
              </Container>
            </main>
          </div>
        </>
      )}

      {canPrescribe && (
        <MedicalPrescriptionForm
          show={showPrescriptionModal}
          onHide={() => setShowPrescriptionModal(false)}
          onSuccess={() => {
            setShowPrescriptionModal(false);
            bumpDashboardStats();
            toast('success', 'Ordonnance enregistrée.');
          }}
        />
      )}

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
